import React, { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { StatusBadge } from '../components/common/StatusBadge';
import { PlatformIcon, PLATFORMS } from '../components/common/PlatformIcon';
import { EmptyState } from '../components/common/EmptyState';
import { accountsApi } from '../lib/api';
import { Plus, Edit, Trash2, Users } from 'lucide-react';
import { toast } from 'sonner';

const initialForm = {
  platform: '',
  display_name: '',
  username: '',
  avatar_url: '',
  status: 'active',
  posting_enabled: true,
  notes: '',
};

export default function CompanyAccounts() {
  const { companyId } = useOutletContext();
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(initialForm);

  const { data, isLoading } = useQuery({
    queryKey: ['accounts', companyId],
    queryFn: () => accountsApi.list(companyId).then((r) => r.data),
  });

  const createMutation = useMutation({
    mutationFn: (data) => accountsApi.create(companyId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts', companyId] });
      toast.success('Account added');
      setDialogOpen(false);
      setForm(initialForm);
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to create'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => accountsApi.update(companyId, id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts', companyId] });
      toast.success('Account updated');
      setDialogOpen(false);
      setEditId(null);
      setForm(initialForm);
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to update'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => accountsApi.delete(companyId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts', companyId] });
      toast.success('Account deleted');
    },
  });

  const handleEdit = (account) => {
    setEditId(account._id);
    setForm({
      platform: account.platform,
      display_name: account.display_name,
      username: account.username,
      avatar_url: account.avatar_url || '',
      status: account.status,
      posting_enabled: account.posting_enabled,
      notes: account.notes || '',
    });
    setDialogOpen(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.platform || !form.display_name || !form.username) {
      toast.error('Platform, display name, and username are required');
      return;
    }
    if (editId) {
      updateMutation.mutate({ id: editId, data: form });
    } else {
      createMutation.mutate(form);
    }
  };

  const openNew = () => {
    setEditId(null);
    setForm(initialForm);
    setDialogOpen(true);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold font-display">Social Accounts</h2>
          <p className="text-sm text-muted-foreground mt-1">Manage connected social media accounts</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} data-testid="add-account-button">
              <Plus className="w-4 h-4 mr-2" /> Add Account
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>{editId ? 'Edit Account' : 'Add Social Account'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Platform *</Label>
                <Select value={form.platform} onValueChange={(v) => setForm((p) => ({ ...p, platform: v }))}>
                  <SelectTrigger data-testid="account-platform-select">
                    <SelectValue placeholder="Select platform" />
                  </SelectTrigger>
                  <SelectContent>
                    {PLATFORMS.map((p) => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Display Name *</Label>
                  <Input
                    value={form.display_name}
                    onChange={(e) => setForm((p) => ({ ...p, display_name: e.target.value }))}
                    placeholder="Brand Name"
                    data-testid="account-display-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Username *</Label>
                  <Input
                    value={form.username}
                    onChange={(e) => setForm((p) => ({ ...p, username: e.target.value }))}
                    placeholder="@username"
                    data-testid="account-username-input"
                  />
                </div>
              </div>
              <div className="flex items-center justify-between">
                <Label>Posting Enabled</Label>
                <Switch
                  checked={form.posting_enabled}
                  onCheckedChange={(v) => setForm((p) => ({ ...p, posting_enabled: v }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={form.notes}
                  onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
                  placeholder="Optional notes"
                  rows={2}
                />
              </div>
              <DialogFooter>
                <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                  {createMutation.isPending || updateMutation.isPending ? 'Saving...' : editId ? 'Update' : 'Add Account'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {isLoading ? (
        <div className="space-y-3">{[1, 2, 3].map((i) => <div key={i} className="h-16 bg-muted animate-pulse rounded-lg" />)}</div>
      ) : data?.accounts?.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {data.accounts.map((account) => (
            <Card key={account._id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <PlatformIcon platform={account.platform} size="md" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm">{account.display_name}</p>
                    <p className="text-xs text-muted-foreground">{account.username}</p>
                  </div>
                  <StatusBadge status={account.status} />
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEdit(account)}>
                      <Edit className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive"
                      onClick={() => deleteMutation.mutate(account._id)}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
                {account.notes && (
                  <p className="text-xs text-muted-foreground mt-2 pl-12">{account.notes}</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <EmptyState
          icon={Users}
          title="No accounts connected"
          description="Add social media accounts to start managing content for this company."
          actionLabel="Add Account"
          onAction={openNew}
        />
      )}
    </div>
  );
}
